import React, { Suspense, useCallback, useEffect, useState } from 'react';

import { MediaQueries } from '../../themes/_declarations/mediaQueries';
import { ThemePropsType } from '../../themes/_declarations/theme';
import Abarth from '../../themes/Abarth';
import AlfaRomeo from '../../themes/AlfaRomeo';
import Citroen from '../../themes/Citroen';
import DS from '../../themes/DS';
import Ecommerce from '../../themes/Ecommerce';
import Fiat from '../../themes/Fiat';
import Jeep from '../../themes/Jeep';
import Lancia from '../../themes/Lancia';
import { loadMediaQueries } from '../../themes/MainTheme';
import Opel from '../../themes/Opel';
import Peugeot from '../../themes/Peugeot';
import GlobalStyles from './globalStyles';
import { ProviderProps } from './types';

const ThemesLoader: {
  [key: string]: React.ComponentType<ThemePropsType>;
} = {
  Abarth,
  AlfaRomeo,
  Citroen,
  DS,
  Ecommerce,
  Fiat,
  Jeep,
  Lancia,
  Opel,
  Peugeot
};

type ThemeComponentType = {
  children: Element | Element[];
  extraProps?: any;
  mediaQueries: MediaQueries;
  themeName: string;
};

const ThemeComponent: React.FC<ThemeComponentType> = ({
  children,
  extraProps,
  mediaQueries,
  themeName
}) => {
  const parsedThemeName = themeName === 'Vauxhall' ? 'Opel' : themeName;

  const Loader =
    parsedThemeName && ThemesLoader[parsedThemeName]
      ? ThemesLoader[parsedThemeName]
      : ThemesLoader.AlfaRomeo;

  return (
    <Suspense>
      <Loader extraProps={extraProps} mediaQueries={mediaQueries}>
        <GlobalStyles />
        {children}
      </Loader>
    </Suspense>
  );
};

const Provider: React.FC<ProviderProps> = React.memo(
  ({ children, forceMobile, idRoot, themeName }) => {
    const [mediaQueries, setMediaQueries] = useState<MediaQueries>({
      isDesktop: false,
      isDesktopExtraLarge: false,
      isDesktopLarge: false,
      isDesktopMedium: false,
      isDesktopSmall: false,
      isMobile: false,
      isMobileMedium: false,
      isMobileSmall: false,
      isPortrait: false,
      isTablet: false
    });

    const updateMediaQueries = useCallback(() => {
      const newMediaQueries = loadMediaQueries();
      setMediaQueries((prevQueries) => {
        const updatedQueries = {
          ...prevQueries,
          ...newMediaQueries,
          isMobile: forceMobile || newMediaQueries.isMobile
        };
        return JSON.stringify(prevQueries) !== JSON.stringify(updatedQueries)
          ? updatedQueries
          : prevQueries;
      });
    }, [forceMobile]);

    useEffect(() => {
      updateMediaQueries();
      window.addEventListener('resize', updateMediaQueries);
      return () => {
        window.removeEventListener('resize', updateMediaQueries);
      };
    }, [updateMediaQueries]);

    return (
      <ThemeComponent
        extraProps={{ idRoot }}
        mediaQueries={mediaQueries}
        themeName={themeName}
      >
        {children}
      </ThemeComponent>
    );
  }
);

export default Provider;
